import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Row, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { fetchCategories } from "../redux/actions/categoryActions";
import { rockGemsApi } from "../services/rockGemsApi";
import { normalizePaginate } from "../utils/normalizePaginate";
import ErrorAlert from "../components/common/ErrorAlert";
import LoadingBlock from "../components/common/LoadingBlock";
import PaginationBar from "../components/common/PaginationBar";

function getCategoryUid(c) {
  return c?.uid || c?.cms_uid || c?.cmsUid || c?.category_uid || c?.slug || c?.key;
}

function getCategoryTitle(c) {
  return c?.title || c?.name || c?.label || getCategoryUid(c) || "Category";
}

function getGemUid(g) {
  return g?.uid || g?.id || g?.gem_uid;
}

function getGemTitle(g) {
  return g?.title || g?.name || g?.label || getGemUid(g) || "Gem";
}

export default function CategoryDetailPage() {
  const { category_uid } = useParams();
  const dispatch = useDispatch();
  const categories = useSelector((s) => s.categories.items);

  const category = useMemo(() => {
    return (categories || []).find((c) => getCategoryUid(c) === category_uid) || null;
  }, [categories, category_uid]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gems, setGems] = useState([]);
  const [meta, setMeta] = useState({});

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const payload = await rockGemsApi.getGems({
          category_uid,
          page,
          items_per_page: 20
        });
        const normalized = normalizePaginate(payload);
        if (!cancelled) {
          setGems(normalized.items);
          setMeta(normalized.meta);
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [category_uid, page]);

  const title = category ? getCategoryTitle(category) : category_uid;

  return (
    <div>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">Category</div>
              <h2 className="page-title d-flex align-items-center gap-2">
                <span>{title}</span>
                <Badge bg="secondary" className="badge-pill">
                  {category_uid}
                </Badge>
              </h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <Button as={Link} variant="outline-secondary" to="/categories">
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Row className="row-deck row-cards">
        <Col xs={12}>
          <Card>
            <Card.Header>
              <Card.Title>Gems</Card.Title>
            </Card.Header>
            <Card.Body>
              <ErrorAlert error={error} title="Failed to load gems" />
              {loading ? (
                <LoadingBlock label="Loading gems..." />
              ) : (
                <>
                  <div className="table-responsive">
                    <Table hover className="table-vcenter table-songsnap">
                      <thead>
                        <tr>
                          <th style={{ width: 220 }}>UID</th>
                          <th>Name</th>
                          <th style={{ width: 140 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {(gems || []).map((g) => {
                          const uid = getGemUid(g);
                          return (
                            <tr key={uid || JSON.stringify(g)}>
                              <td className="text-muted small">{uid}</td>
                              <td className="fw-bold">{getGemTitle(g)}</td>
                              <td className="text-end">
                                <Button
                                  as={Link}
                                  size="sm"
                                  variant="primary"
                                  to={`/gems/${uid}?category_uid=${encodeURIComponent(category_uid)}`}
                                >
                                  Detail
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                        {!gems?.length ? (
                          <tr>
                            <td colSpan={3} className="text-center text-muted py-4">
                              No gems found.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </Table>
                  </div>
                  <PaginationBar
                    page={page}
                    onPageChange={(p) => setPage(Math.max(1, p))}
                    canPrev={page > 1}
                    // If API doesn't return total, assume next is possible when we got full page size
                    canNext={
                      typeof meta.total === "number" && typeof meta.items_per_page === "number"
                        ? page * meta.items_per_page < meta.total
                        : (gems || []).length >= 20
                    }
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

